import { alpha } from '@mui/material/styles';
import { colors, portalAccents, type PortalAccent } from '@munifocus/tokens';

// Raw constants pulled from the framework-agnostic token source.
const WHITE = colors.common.white;
const BLACK = colors.common.black;

/**
 * Build the Munifocus design palette for one portal.
 *
 * The raw hex primitives live in `@munifocus/tokens` (shared with mobile); this
 * derives the MUI palette and adds the alpha-based variants used only on the
 * web. Apps consume a palette (directly or through their derived app-theme
 * alias) — no raw hex/rgba literals in app code.
 *
 * **Why this is a function.** Every portal shares the same ink, cream rest,
 * hairlines and type; what differs is one accent family. The three portals were
 * previously pixel-identical, which meant an operator with the Government
 * Portal, the Client Portal and the admin tooling open in three tabs had
 * nothing but the URL to tell them apart. Everything that can be derived from
 * the accent — the focus rings, the sticky-header tint, the card shadow — is
 * derived here rather than restated per portal, so adding a fourth portal is a
 * new entry in `portalAccents` and nothing else.
 */
export function makePalette(family: PortalAccent) {
  return {
    brand: family,
    text: {
      primary: colors.text.primary,
      secondary: colors.text.secondary,
      muted: colors.text.muted,
      /** Muted text when set against a portal rail */
      mutedOnDark: colors.text.mutedOnDark,
      /**
       * The white-on-dark text ramp.
       *
       * Every step here is *text*, so every step has to clear 4.5:1 against the
       * darkest surface it is used on — the portal's `rail`, its header, footer
       * and hero colour. The tail of the ramp did not: `onDarkDim` (0.5)
       * measured 4.23:1 and `onDarkGhost` (0.35) measured 2.85:1, and axe found
       * ~120 instances of the two across the marketing site. Raised to the
       * lowest opacity that clears AA on deep teal (0.56 -> 4.62:1) — the
       * lightest of the three rails, so the ramp holds for all of them —
       * keeping the visual ordering intact.
       */
      onDark: WHITE,
      onDarkStrong: alpha(WHITE, 0.88),
      onDarkSoft: alpha(WHITE, 0.85),
      onDarkMuted: alpha(WHITE, 0.8),
      onDarkSubtle: alpha(WHITE, 0.72),
      onDarkFaint: alpha(WHITE, 0.64),
      onDarkDim: alpha(WHITE, 0.6),
      onDarkGhost: alpha(WHITE, 0.56),
    },
    background: {
      /** Cream page rest */
      default: colors.surface.cream,
      paper: colors.surface.white,
      /** Parchment accent zone */
      parch: colors.surface.parch,
      surfaceOnDarkFaint: alpha(WHITE, 0.08),
      surfaceOnDarkSoft: alpha(WHITE, 0.1),
      surfaceOnDarkMuted: alpha(WHITE, 0.12),
    },
    border: {
      onCream: colors.border.onCream,
      onWhite: colors.border.onWhite,
      /** Hairline rule on cream / white surfaces */
      line: colors.border.line,
      /** Stronger hairline — input borders, secondary-button outline */
      lineStrong: colors.border.lineStrong,
      /** Subtle outer ring of a brandmark on a light surface */
      onLight: colors.border.onLight,
      /** 3:1 boundary for focusable controls (WCAG 1.4.11) */
      interactive: colors.border.interactive,
      /** Focus indicator on light surfaces — this portal's accent */
      focus: family.accent,
      /** Focus indicator on this portal's rail */
      focusOnDark: family.accentLight,
      onDarkFaint: alpha(WHITE, 0.08),
      onDarkSubtle: alpha(WHITE, 0.12),
      onDarkSoft: alpha(WHITE, 0.13),
      onDarkMuted: alpha(WHITE, 0.18),
      onDarkStrong: alpha(WHITE, 0.2),
      onDarkInput: alpha(WHITE, 0.25),
      onDarkButton: alpha(WHITE, 0.28),
      onDarkHover: alpha(WHITE, 0.5),
    },
    semantic: colors.semantic,
    overlay: {
      /** Sticky header tint over the rail at 94% opacity */
      railSticky: alpha(family.rail, 0.94),
      /** Translucent white for frosted/blurred sticky surfaces on light backgrounds */
      whiteSticky: alpha(WHITE, 0.85),
      /** Dialog backdrop — near-black with the portal's undertone */
      backdrop: alpha(family.railAbyss, 0.72),
      /** 1px grid lines on dark hero / CTA surfaces */
      gridLineOnDark: alpha(WHITE, 0.05),
      /** Repeating stripe on dark device-mockup surfaces */
      gridStripeOnDark: alpha(WHITE, 0.045),
      /** Card hover shadow tinted with the rail */
      cardShadow: alpha(family.rail, 0.4),
      /** Heavy drop shadow for dark device frames */
      blackShadow: alpha(BLACK, 0.6),
      /** Focus ring around accent-filled inputs */
      accentFocusRing: alpha(family.accentLight, 0.15),
    },
  } as const;
}

/**
 * The default palette — the Government Portal's teal.
 *
 * This is what a surface belonging to no particular portal reads: the
 * marketing site, and any shared component rendered outside a portal shell.
 */
export const palette = makePalette(portalAccents.gov);

/** The Client Portal's palette — indigo. */
export const publicPalette = makePalette(portalAccents.public);

/** The admin portal's palette — petrol blue. */
export const adminPalette = makePalette(portalAccents.admin);

/** @public — palette type kept as part of the theme's public surface. */
export type Palette = typeof palette;

/**
 * The colours of the MuniFocus mark itself.
 *
 * The logo is the company's, not the portal's: the rail behind it changes per
 * portal, the mark never does. `docs/design-system/README.md` puts it plainly —
 * "the arc mark always carries the brighter teal".
 *
 * This exists as its own named export because the rule kept being broken by
 * accident. The lockup is drawn in seven places (a `BrandMark` per app, plus
 * the auth shells that draw their own), and the sweep that gave each app its
 * own palette repainted the logo along with the chrome in every one of them.
 * Reaching for `palette.brand.accent` inside a logo is the easy mistake;
 * reaching for `brandMark.onLight` is not, and unlike the former it is
 * greppable, so the guard in `portal-accent.spec.ts` can hold the rule.
 */
export const brandMark = {
  /** The mark on a portal rail or any dark brand panel. */
  onDark: portalAccents.gov.accentLight,
  /** The mark on white, cream or any light surface. */
  onLight: portalAccents.gov.accent,
} as const;
